// /en/education/documents — Wave 70: EN hub of education document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/education/documents";
const URL = `${SITE}${PATH}`;
const TH_URL = `${SITE}/education/documents`;
const TITLE = "Thai Education Documents — 6 Cases | NYC";
const DESC =
  "Thai and foreign education documents in six cases: degrees and transcripts for study abroad, recognition of foreign qualifications, school records, student-visa sets, professional licences and certified translations.";

export const Route = createFileRoute("/en/education/documents/")({
  loader: async () => {
    const { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_EN, EDU_AUTHORITY_EN } = await import("@/data/education-doc-groups");
    return {
      __d: { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_EN, EDU_AUTHORITY_EN },
      groups: EDUCATION_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: TH_URL },
      { rel: "alternate", hrefLang: "x-default", href: TH_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Thai and foreign education document cases",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: EducationDocumentsEnPage,
});

function EducationDocumentsEnPage() {
  const { __d: { EDUCATION_DOC_GROUPS, EDU_APOSTILLE_EN, EDU_AUTHORITY_EN } } = Route.useLoaderData() as { __d: typeof import("@/data/education-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">Education documents / Cases</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Thai Education Documents — 6 Cases</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Education paperwork follows different routes depending on who issued the document and who will receive it. This
        hub splits the work into six cases, each with the documents to prepare, the order of certification and a
        pre-filing checklist. Some requirements vary by institution and destination country, so confirm the current
        rules with the issuing authority before you file.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{EDU_AUTHORITY_EN}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{EDU_APOSTILLE_EN}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {EDUCATION_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/en/education/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {EDUCATION_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/education/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full {g.titleEn} guide →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Send scans of your documents by LINE @nycli, phone +66-83-249-4999 or email nycli@ilc.ltd and our team will
        confirm the steps, anything still missing and a realistic timeline for your case.
      </p>
          <ServiceAuthorityBlock topic="education" locale="en" />
    </main>
  );
}
